import * as joda from "@js-joda/core";
import currency from "currency.js";
import { match } from "ts-pattern";
import {
  creditnoteid,
  enrollment_status,
  enrollmentid,
  enrollmentrequestid,
  expectedpaymentid,
  formulachargemode,
  formulaid,
  iban,
  instrumentid,
  internalteacherid,
  invoiceid,
  lesson_location,
  lessongroupid,
  mollie_payment_id,
  payment_method,
  payment_scheme_id,
  paymentid,
  periodic_payment_method,
  periodid,
  price_adapted_to_lg,
  price_adapted_to_lg_and_student,
  price_formula,
  roomid,
  student_charge_id,
  student_period_fee_id,
  teacherid,
  weekday,
} from "../../../sql/classy/types";
import { enrollmentrequest_full } from "../../pages/approve_enrollment_request/server";
import { invoicelinetype } from "../../parsing_from_ur";
import { basicStudent } from "../students";

export type invoicelineFormula = {
  description: string;
  amount_with_vat: price_formula;
  amount_without_vat: price_formula | null;
  chargemode: formulachargemode;
  vat: number;
  type_: invoicelinetype;
};

export type invoicelineLG = {
  description: string;
  amount_with_vat: price_adapted_to_lg;
  amount_without_vat: price_adapted_to_lg | null;
  chargemode: formulachargemode;
  vat: number;
  type_: invoicelinetype;
};

export type invoicelineStudent = {
  description: string;
  amount: price_adapted_to_lg_and_student;
  vat: number;
};

export type invoicelineEachPeriod = {
  description: string;
  amount_with_vat: price_adapted_to_lg;
  amount_without_vat: price_adapted_to_lg | null;
  vat: number;
  from_each_period_fee: true;
  type_: invoicelinetype;
};

export type invoicelineRequestedOptional = {
  description: string;
  from_each_period_fee: boolean;
};

export type accountInfoClient =
  | {
      tag: "not_applicable";
    }
  | {
      tag: "applicable";
      iban: iban;
      name_on_iban: string;
    };

export type invoiceFull = {
  id: invoiceid;
  externalid: number;
  enrollmentrequestid: enrollmentrequestid | null;
  enrollmentid: enrollmentid | null;
  student_period_fee_id: student_period_fee_id | null;
  student_charge_id: student_charge_id | null;
  externalidstring: string;
  accountingsoftwareidentifier: string | null;
  amount_total: currency;
  invoicelines:
    | {
        amount: price_adapted_to_lg_and_student;
        description: string;
        vat: number;
        ordering: number;
      }[]
    | null;
  amount_paid: currency;
  payments:
    | {
        id: paymentid;
        amount: currency;
        date: joda.LocalDate;
        paymentproviderid: mollie_payment_id | null;
        method: payment_method;
      }[]
    | null;
  amount_credited: currency;
  creditnotes:
    | {
        id: creditnoteid;
        amount: currency;
        vat: number;
        externalid: number;
        date: joda.LocalDate;
        accountingsoftwareidentifier: string | null;
      }[]
    | null;
  amount_left: currency;
  payment_scheme_id: payment_scheme_id | null;
  expectedpayments:
    | {
        id: expectedpaymentid;
        amount: currency;
        date: joda.LocalDate;
        amount_paid: currency;
        amount_left: currency;
        periodic_payment_method: periodic_payment_method | null;
      }[]
    | null;
};

export type invoiceSubject =
  | {
      tag: "enrollment";
      invoiceid: invoiceid;
      created: joda.LocalDate;
      status: enrollment_status;
      lg: {
        id: lessongroupid;
        firstlesson: joda.LocalDate | null;
        lastlesson: joda.LocalDate | null;
        time: joda.LocalTime;
        weekday: weekday;
        roomid: roomid | null;
        location: lesson_location;
        startsinperiod: periodid;
        formula: { description: string; id: formulaid };
      };
      room_description: string | null;
      instrument: { id: instrumentid; description: string };
      student: basicStudent;
      teacher: {
        id: teacherid;
        internalteacherid: internalteacherid;
        firstname: string;
        lastname: string;
      };
      id: enrollmentid;
      period: {
        id: periodid;
        firstday: joda.LocalDate;
        lastday: joda.LocalDate;
      };
    }
  | {
      tag: "periodfee";
      invoiceid: invoiceid;
      id: student_period_fee_id;
      created: joda.LocalDate;
      student: basicStudent;
      period: {
        id: periodid;
        firstday: joda.LocalDate;
        lastday: joda.LocalDate;
      };
    }
  | {
      tag: "student_charge";
      invoiceid: invoiceid;
      created: joda.LocalDate;
      student: basicStudent;
      student_charge: {
        id: student_charge_id;
        description: string;
      };
      period: {
        id: periodid;
        firstday: joda.LocalDate;
        lastday: joda.LocalDate;
      };
    }
  | {
      tag: "enrollmentrequest";
      invoiceid: invoiceid;
      created: joda.LocalDate;
      student: basicStudent;
      period: {
        id: periodid;
        firstday: joda.LocalDate;
        lastday: joda.LocalDate;
      };
      enr_req: enrollmentrequest_full;
    };

export type invoiceSubjectFilter =
  | { tag: "all" }
  | { tag: "periodfee" }
  | { tag: "enrollment"; teachers: { internalteacherid: internalteacherid }[] }
  | { tag: "student_charge" };

export function invoiceSubjectToEnrollmentId(
  s: invoiceSubject,
): enrollmentid | null {
  return match(s)
    .with({ tag: "enrollment" }, (s) => s.id)
    .with({ tag: "student_charge" }, () => null)
    .with({ tag: "periodfee" }, () => null)
    .with({ tag: "enrollmentrequest" }, () => null)
    .exhaustive();
}

export function invoiceSubjectToFirstLesson(
  s: invoiceSubject,
): joda.LocalDate | null {
  return match(s)
    .with({ tag: "enrollment" }, (s) => s.lg.firstlesson)
    .with({ tag: "enrollmentrequest" }, (s) => s.enr_req.contents.firstlesson)
    .with({ tag: "student_charge" }, () => null)
    .with({ tag: "periodfee" }, () => null)
    .exhaustive();
}

export function invoiceSubjectToLastLesson(
  s: invoiceSubject,
): joda.LocalDate | null {
  return match(s)
    .with({ tag: "enrollment" }, (s) => s.lg.lastlesson)
    .with({ tag: "enrollmentrequest" }, (s) => s.enr_req.contents.lastlesson)
    .with({ tag: "student_charge" }, () => null)
    .with({ tag: "periodfee" }, () => null)
    .exhaustive();
}
